<template>
  <div>
    <LeadTagSidebar />
    <RoadshowScoreReviewSidebar />
    <DisqualifyLeadSidebar />
    <CommentSidebar />
    <SettingsTableKanbanSidebar
      :search-kanban="fetchKanbanData"
      :search-table="fetchTableData"
      :view="view"
    />
    <SaveConsultantBasicInformationSidebar />
    <b-row>
      <b-col
        cols="12"
        md="8"
        class="mb-1"
      >
        <b-card
          no-body
          class="h-100 height-card overflow-hidden"
        >
          <b-card-body class="d-flex flex-column pb-0">
            <div class="w-100">
              <h5 class="mb-1">
                Balanço de recrutamento
              </h5>
              <b-form class="row mb-0">
                <b-form-group
                  label="Data"
                  label-for="searchCreatedAt"
                  class="mb-0 col-4"
                  style="min-width: 220px"
                >
                  <flat-pickr
                    id="searchCreatedAt"
                    class="form-control"
                    :config="flatPickrConfig"
                    :placeholder="`01 Jan 2020 até 31 Dez ${actualYear}`"
                    :value="createdAt"
                    @input="handleDateChange"
                  />
                </b-form-group>
                <b-form-group
                  label="Franqueado"
                  label-for="statementFranchise"
                  class="mb-0 col-4"
                  style="min-width: 220px"
                >
                  <v-select
                    id="statementFranchise"
                    v-model="statementFranchise"
                    multiple
                    :reduce="(franchise) => franchise.id"
                    :options="franchises"
                    :loading="loadingFranchises"
                    label="display_name"
                    @input="handleFilterChange"
                  >
                    <template #no-options="{ search, searching, loading }">
                      Sem resultados
                    </template>
                  </v-select>
                </b-form-group>
                <b-form-group
                  label="Dono do Lead"
                  label-for="statementConsultants"
                  class="mb-0 col-4"
                  style="min-width: 220px"
                >
                  <v-select
                    id="statementConsultants"
                    v-model="statementConsultants"
                    multiple
                    :reduce="(consultant) => consultant.user_id"
                    :options="allConsultants"
                    :loading="loadingAllConsultants"
                    label="name"
                    @input="handleFilterChange"
                  >
                    <template #no-options="{ search, searching, loading }">
                      Sem resultados
                    </template>
                  </v-select>
                </b-form-group>
              </b-form>
            </div>
            <ChartRecruitmentStatement
              :statement="statement"
              :loading="loadingStatement"
            />
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        md="4"
        class="mb-1"
      >
        <b-card
          no-body
          class="h-100 height-card"
        >
          <b-card-body class="d-flex flex-column pb-0">
            <LeadDetails
              ref="leadDetails"
              :fetch-lead-data="fetchLeadData"
              :loading="loadingLeadDetails"
            />
          </b-card-body>
        </b-card>
      </b-col>
      <b-col cols="12">
        <RecruitmentFilters
          ref="filters"
          :fetch-data="fetchData"
          :loading-search="loading"
          :view="view"
        />
      </b-col>
      <b-col cols="12">
        <div class="tabs">
          <div
            :class="[
              'tabs-group',
              skin === 'dark' ? 'tabs-group-dark' : 'tabs-group-light',
            ]"
          >
            <button
              class="tab-button"
              :class="{ active: view === 'kanban' }"
              @click="setView('kanban')"
            >
              <feather-icon
                icon="ColumnsIcon"
                size="16"
              />
              Kanban
            </button>
            <button
              class="tab-button"
              :class="{ active: view === 'tabela' }"
              @click="setView('tabela')"
            >
              <feather-icon
                icon="CreditCardIcon"
                size="16"
              />
              Tabela
            </button>
          </div>
        </div>
      </b-col>
      <b-col cols="12">
        <template v-if="view === 'kanban'">
          <b-col
            v-if="loading"
            cols="12"
          >
            <div class="d-flex justify-content-center m-2">
              <b-spinner
                variant="primary"
                style="width: 3rem; height: 3rem"
              />
            </div>
          </b-col>
          <b-col
            v-else-if="filteredKanbanStages.length > 0"
            cols="12 px-0"
          >
            <div class="kanban-board">
              <ColumnRecruitmentList
                v-for="(stage, index) in filteredKanbanStages"
                :key="`${stage.stage_id}-${stage.lastUpdated}`"
                :stage-name="stage.stage_name"
                :recruitments="stage.recruitments"
                :card-type="stage.stage_name"
                :type="stage.stage_id"
                :totalizer="stage.total"
                :search="fetchKanbanData"
                @toggle-favorite="toggleFavorite"
                @toggle-status="toggleStatus"
                @load-more="increaseLimit"
                @download-resume="resumeDownload"
                @disqualify="disqualifyLead"
                @convert-to-pn="convertToPn"
                @requires-appointment="openAppointmentForMovedRecruitments"
              />
            </div>
          </b-col>
          <b-col
            v-else
            cols="12"
          >
            <div
              class="d-flex justify-content-center m-2"
              style="font-size: 18px"
            >
              Nenhum resultado encontrado.
            </div>
          </b-col>
        </template>
        <template v-else>
          <b-col
            v-if="loading"
            cols="12"
          >
            <div class="d-flex justify-content-center m-2">
              <b-spinner
                variant="primary"
                style="width: 3rem; height: 3rem"
              />
            </div>
          </b-col>
          <b-col
            v-else-if="sortedTableStages.length > 0"
            cols="12 px-0"
          >
            <div class="kanban-board flex-column">
              <TableRecruitmentList
                v-for="(stage, index) in sortedTableStages"
                :key="`${stage.stage_id}-${index}`"
                :stage-name="stage.stage_name"
                :recruitments="stage.data"
                :totalizer="stage.total"
                :loading="loading"
                :total-items="stage.total"
                :stage-percentage="stagePercentage(stage)"
                :per-page="stage.per_page"
                :type="stage.stage_id"
                :selected-page="stage.current_page"
                stage-i-d="221"
                @page-change="handlePageChange(stage.stage_id, $event)"
                @refresh="fetchTableData"
                @toggle-favorite="toggleFavorite"
                @toggle-select="toggleSelect"
                @toggle-status="toggleStatus"
                @convert-to-pn="convertToPn"
                @disqualify="disqualifyLead"
              />
            </div>
          </b-col>
          <b-col
            v-else
            cols="12"
          >
            <div
              class="d-flex justify-content-center m-2"
              style="font-size: 18px"
            >
              Nenhum resultado encontrado.
            </div>
          </b-col>
        </template>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import todayButton from '@/helpers/todayButton'
import CommentSidebar from '@/modules/shared/components/CommentSidebar.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import * as statusTypes from '../../lead/store/types';
import { useToast } from 'vue-toastification/composition';
import useAppConfig from '@core/app-config/useAppConfig';
import * as customerStatus from '@/constants/customers_status';
import ToastificationContent from '@core/components/toastification/ToastificationContent';
import { getRangeDates } from '@/helpers/date_picker';
import { getDigitalFranchiseDisplayName } from '@/helpers/franchise';
import SaveConsultantBasicInformationSidebar from '@/modules/consultant/components/SaveConsultantBasicInformationSidebar.vue';
import { OPEN_NEW_CONSULTANT_SIDEBAR } from '@/modules/consultant/store/types';
import { OPEN_NEW_APPOINTMENT_SIDEBAR } from '@/modules/appointment/store/types';
import { STAGE_ORDER } from '@/constants/recruitment_stages';
import * as appointmentTypes from '@/constants/appointment_types';
import * as sharedTypes from '@/modules/shared/store/types';
import vSelect from 'vue-select';
import { debounce } from 'lodash';
import * as types from '../store/types';
import RecruitmentFilters from '../components/RecruitmentFilters.vue'
import DisqualifyLeadSidebar from '../components/DisqualifyLeadSidebar.vue'
import TableRecruitmentList from '../components/TableRecruitmentList.vue'
import ColumnRecruitmentList from '../components/ColumnRecruitmentList.vue'
import LeadTagSidebar from '../components/LeadTagSidebar.vue'
import RoadshowScoreReviewSidebar from '../components/RoadshowScoreReviewSidebar.vue'
import SettingsTableKanbanSidebar from '../components/SettingsTableKanbanSidebar.vue'
import LeadDetails from '../components/LeadDetails.vue'
import ChartRecruitmentStatement from '../components/ChartRecruitmentStatement.vue'

export default {
  components: {
    ChartRecruitmentStatement,
    LeadDetails,
    LeadTagSidebar,
    RoadshowScoreReviewSidebar,
    ColumnRecruitmentList,
    DisqualifyLeadSidebar,
    RecruitmentFilters,
    TableRecruitmentList,
    flatPickr,
    CommentSidebar,
    SettingsTableKanbanSidebar,
    SaveConsultantBasicInformationSidebar,
    vSelect,
  },
  setup() {
    const { skin } = useAppConfig()
    const actualYear = new Date().getFullYear()
    return { toast: useToast(), skin, actualYear }
  },

  data() {
    return {
      statement: [],
      franchises: [],
      allConsultants: [],
      usersRecruitments: [],
      loadingStatement: false,
      loadingFranchises: false,
      loadingAllConsultants: false,
      loading: false,
      tableRequestId: 0,
      createdAt: null,
      statementFranchise: [],
      statementConsultants: [],
      lastFetchedFilters: null,
      flatPickrConfig: {
        altFormat: 'j M Y',
        altInput: true,
        mode: 'range',
        dateFormat: 'Y-m-d',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin(todayButton)],
      },
      limits: [
        { stage_id: 1, limit: 5 },
        { stage_id: 2, limit: 5 },
        { stage_id: 3, limit: 5 },
        { stage_id: 4, limit: 5 },
        { stage_id: 5, limit: 5 },
        { stage_id: 6, limit: 5 },
        { stage_id: 7, limit: 5 },
        { stage_id: 8, limit: 5 },
      ],
      view: 'kanban',
      order: undefined,
      loadingLeadDetails: false,
      currentFilters: {
        general: undefined,
        name: undefined,
        stage: undefined,
        status: [],
        origin: undefined,
        franchise: undefined,
        potential: undefined,
        availability: undefined,
        createdAt: undefined,
        callReminder: undefined,
        structureOption: undefined,
        favorite: false,
        disqualified: false,
        added_by: undefined,
      },
      lastFetchedFilters: null,
    }
  },

  computed: {
    ...mapGetters({
      kanbanStages: types.RECRUTMENT_KANBAN,
      settingsTableKanban: types.SETTINGS_TABLE_KANBAN,
      tableStages: types.RECRUTMENT_TABLE,
      consultants: sharedTypes.ALL_CONSULTANTS,
    }),

    filteredKanbanStages() {
      if (!Array.isArray(this.kanbanStages)) return []
      return this.kanbanStages.filter(stage => this.isStageVisible(stage.stage_name))
    },

    filteredTableStages() {
      if (!Array.isArray(this.tableStages)) return []
      const uniqueStages = this.tableStages.reduce((acc, stage) => {
        if (!acc.find(s => s.stage_id === stage.stage_id)) {
          acc.push(stage)
        }
        return acc
      }, [])
      return uniqueStages.filter(stage => (
        stage.stage_id !== 3 && this.isStageVisible(stage.stage_name)
      ))
    },

    sortedTableStages() {
      const stageOrder = STAGE_ORDER
      if (!Array.isArray(this.filteredTableStages)) return []
      return this.filteredTableStages.slice().sort((a, b) => {
        const indexA = stageOrder.indexOf(a.stage_name)
        const indexB = stageOrder.indexOf(b.stage_name)
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
      })
    },

    status() {
      return customerStatus
    },

    calcStagePercentages() {
      const total = this.statement.reduce((sum, item) => {
        const value = Number(item.value)
        return sum + (Number.isFinite(value) ? value : 0)
      }, 0)
      return this.statement.map(item => {
        const value = Number(item.value)
        const safeValue = Number.isFinite(value) ? value : 0
        const percentage = total ? (safeValue / total) * 100 : 0
        const safePercentage = Number.isFinite(percentage)
          ? Math.min(Math.max(percentage, 0), 100)
          : 0
        return {
          category: item.category,
          percentage: parseFloat(safePercentage.toFixed(2)),
          remainingPercentage: parseFloat((100 - safePercentage).toFixed(2)),
        }
      })
    },
  },

  mounted() {
    const today = new Date()
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1)
    this.createdAt = [
      firstDayOfYear.toISOString().split('T')[0],
      today.toISOString().split('T')[0],
    ]
    this.getSettingsTableKanban()
    this.fetchFranchises()
    this.fetchConsultants()
    this.fetchKanbanData()
    this.fetchStatementDataWithFilters()
  },

  methods: {
    ...mapActions({
      getStatement: types.GET_BALANCE_STATEMENT,
      searchRecruitmentKanban: types.SEARCH_RECRUITMENT_KANBAN,
      searchRecruitmentTableGrouped: types.SEARCH_RECRUITMENT_TABLE_GROUPED,
      setFavorite: types.SET_FAVORITE,
      setCustomerLeadStatus: statusTypes.SET_CUSTOMER_LEAD_STATUS,
      downloadResume: statusTypes.DOWNLOAD_RESUME,
      openDisqualifyLeadSidebar: types.OPEN_DISQUALIFY_LEAD_SIDEBAR,
      getSettingsTableKanban: types.GET_SETTINGS_TABLE_KANBAN,
      openSaveConsultantBasicInformationSidebar: OPEN_NEW_CONSULTANT_SIDEBAR,
      getLeadData: types.GET_BALANCE_LEADDATA,
      getFranchises: sharedTypes.GET_FRANCHISES,
      getConsultants: sharedTypes.GET_ALL_CONSULTANTS,
      openNewAppointmentSidebar: OPEN_NEW_APPOINTMENT_SIDEBAR,
    }),
    ...mapMutations({
      clearTableResults: types.MUTATE_CLEAR_RECRUITMENT_TABLE,
    }),

    isStageVisible(stageName) {
      if (!this.settingsTableKanban) return true
      const stageKeyMap = {
        Leads: 'leads',
        'Road Show': 'roads_show',
        'Dinâmica de Grupo': 'dinamica_de_grupo',
        'Entrevista Individual': 'entrevista_individual',
        'Pré-Job Sample': 'pre_job_sample',
        'Job Sample': 'job_sample',
        'Aprovação Final': 'aprovacao_final',
        'Convertido em PN': 'convertido_em_pn',
      }
      const stageKey = stageKeyMap[stageName]
      return this.settingsTableKanban[stageKey] !== false
    },

    stagePercentage(stage) {
      const percentage = this.calcStagePercentages.find(p => p.category === stage.stage_name)
        ?.percentage || 0
      const safePercentage = Number.isFinite(Number(percentage))
        ? Math.min(Math.max(Number(percentage), 0), 100)
        : 0
      return [safePercentage, 100 - safePercentage]
    },

    handleDateChange(selectedDates) {
      this.createdAt = selectedDates
      this.fetchStatementDataWithFilters()
    },

    handleFilterChange() {
      this.fetchStatementDataWithFilters()
    },

    fetchStatementDataWithFilters: debounce(async function () {
      const currentFilters = JSON.stringify({
        start: this.createdAt[0],
        end: this.createdAt[1],
        franchise: this.statementFranchise,
        consultants: this.statementConsultants,
      })

      if (this.lastFetchedFilters === currentFilters) {
        return
      }

      this.loadingStatement = true
      try {
        const { start, end } = getRangeDates(this.createdAt)
        if (!start || !end) {
          throw new Error('Invalid date range')
        }
        const response = await this.getStatement({
          start_date: start,
          end_date: end,
          franchise: this.statementFranchise,
          consultants: this.statementConsultants,
        })
        this.statement = response || []
        this.lastFetchedFilters = currentFilters
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao trazer o balanço de recrutamento. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        this.statement = []
      } finally {
        this.loadingStatement = false
      }
    }, 500),

    async fetchFranchises() {
      this.loadingFranchises = true
      try {
        const response = await this.getFranchises()
        this.franchises = (response.data || []).map(franchise => ({
          ...franchise,
          display_name:
            getDigitalFranchiseDisplayName(franchise)
            || franchise?.display_name
            || franchise?.name
            || '',
        }))
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os franqueados. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        this.franchises = []
      } finally {
        this.loadingFranchises = false
      }
    },

    async fetchConsultants() {
      this.loadingAllConsultants = true
      try {
        const response = await this.getConsultants()
        this.allConsultants = response.data || []
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os consultores. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        this.allConsultants = []
      } finally {
        this.loadingAllConsultants = false
      }
    },

    async fetchStatementData(start_date, end_date) {
      this.createdAt = [start_date, end_date]
      await this.fetchStatementDataWithFilters()
    },

    fetchLeadData(activeTab) {
      const {
        general,
        name,
        stage,
        status,
        origin,
        franchise,
        potential,
        availability,
        createdAt,
        callReminder,
        structureOption,
        favorite,
        disqualified,
        added_by,
      } = this.$refs.filters
      this.loadingLeadDetails = true
      const tab = activeTab || this.$refs.leadDetails.activeTab
      this.getLeadData({
        filter_type: tab,
        general_search: general,
        name,
        stage,
        status,
        origin,
        franchise,
        potential,
        availability,
        createdAt,
        callReminder,
        structureOption,
        favorite,
        disqualified,
        added_by,
      })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao trazer a lista de planejadores. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loadingLeadDetails = false
        })
    },

    setView(view) {
      this.view = view
      if (view === 'kanban') {
        this.fetchKanbanData()
      } else {
        this.fetchTableData()
      }
    },

    fetchKanbanData() {
      this.loading = true
      const { filters } = this.$refs
      if (!filters) {
        this.loading = false
        console.error('Componente de filtros não encontrado!')
        return
      }

      const filterData = {
        general: filters.general,
        name: filters.name,
        stage: filters.stage,
        status: [...filters.status],
        origin: filters.origin ? [...filters.origin] : undefined,
        franchise: filters.franchise ? [...filters.franchise] : undefined,
        potential: filters.potential ? [...filters.potential] : undefined,
        availability: filters.availability ? [...filters.availability] : undefined,
        createdAt: filters.createdAt,
        callReminder: filters.callReminder,
        structureOption: filters.structureOption,
        favorite: filters.favorite,
        disqualified: filters.disqualified,
        added_by: filters.added_by ? [...filters.added_by] : undefined,
      }

      this.currentFilters = filterData

      const {
        general,
        name,
        stage,
        status,
        origin,
        franchise,
        potential,
        availability,
        createdAt,
        callReminder,
        structureOption,
        favorite,
        disqualified,
        added_by,
      } = filterData
      const call_reminder = getRangeDates(callReminder)
      const created_at = getRangeDates(createdAt)

      this.searchRecruitmentKanban({
        general_search: general,
        name,
        status,
        stage,
        origin,
        structure_option: structureOption,
        franchise,
        potential,
        availability,
        favorite,
        disqualified,
        added_by,
        call_reminder_start: call_reminder.start,
        call_reminder_end: call_reminder.end,
        created_at_start: created_at.start,
        created_at_end: created_at.end,
        limit: this.limits,
      })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao trazer a lista de planejadores. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchTableData: debounce(function () {
      this.loading = true
      const requestId = ++this.tableRequestId
      const { filters } = this.$refs
      if (!filters) {
        this.loading = false
        console.error('Componente de filtros não encontrado!')
        return
      }

      const filterData = {
        general: filters.general,
        name: filters.name,
        stage: filters.stage,
        status: [...filters.status],
        origin: filters.origin ? [...filters.origin] : undefined,
        franchise: filters.franchise ? [...filters.franchise] : undefined,
        potential: filters.potential ? [...filters.potential] : undefined,
        availability: filters.availability ? [...filters.availability] : undefined,
        createdAt: filters.createdAt,
        callReminder: filters.callReminder,
        structureOption: filters.structureOption,
        favorite: filters.favorite,
        disqualified: filters.disqualified,
        added_by: filters.added_by ? [...filters.added_by] : undefined,
      }

      this.currentFilters = filterData

      const {
        general,
        name,
        stage,
        status,
        origin,
        franchise,
        potential,
        availability,
        createdAt,
        callReminder,
        structureOption,
        favorite,
        disqualified,
        added_by,
      } = filterData
      const call_reminder = getRangeDates(callReminder)
      const created_at = getRangeDates(createdAt)
      const uniStage = stage

      const payload = {
        general,
        stage: uniStage,
        name,
        call_reminder_start: call_reminder.start,
        call_reminder_end: call_reminder.end,
        created_at_start: created_at.start,
        created_at_end: created_at.end,
        status,
        structure_option: structureOption,
        franchise,
        potential,
        availability,
        origin,
        favorite,
        disqualified,
        added_by,
        order: this.order,
        limit: 10,
        currentPage: 1,
      }
      this.clearTableResults()
      this.searchRecruitmentTableGrouped(payload)
        .catch(error => {
          if (error?.canceled) return
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao trazer a tabela de planejadores. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          if (requestId === this.tableRequestId) {
            this.loading = false
          }
        })
    }, 500),

    handlePageChange(stageId, page) {
      this.fetchTableDataForStage(stageId, page)
    },

    async fetchTableDataForStage(stageId, page = 1) {
      this.loading = true
      const requestId = ++this.tableRequestId

      const savedFilters = this.currentFilters
      const call_reminder = getRangeDates(savedFilters.callReminder)
      const created_at = getRangeDates(savedFilters.createdAt)

      const payload = {
        general: savedFilters.general,
        stage: stageId,
        name: savedFilters.name,
        call_reminder_start: call_reminder.start,
        call_reminder_end: call_reminder.end,
        created_at_start: created_at.start,
        created_at_end: created_at.end,

        status:
          savedFilters.status && savedFilters.status.length > 0
            ? [...savedFilters.status]
            : [],
        structure_option: savedFilters.structureOption,
        franchise: savedFilters.franchise ? [...savedFilters.franchise] : [],
        potential: savedFilters.potential ? [...savedFilters.potential] : [],
        availability: savedFilters.availability ? [...savedFilters.availability] : [],
        origin: savedFilters.origin ? [...savedFilters.origin] : [],
        favorite: savedFilters.favorite,
        disqualified: savedFilters.disqualified,
        added_by: savedFilters.added_by ? [...savedFilters.added_by] : [],
        order: this.order,
        limit: 10,
        currentPage: page,
      }

      try {
        const response = await this.searchRecruitmentTableGrouped(payload)
        if (response?.canceled) return
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao trazer a tabela de planejadores. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        if (requestId === this.tableRequestId) {
          this.loading = false
        }
      }
    },

    toggleStatus(status_id, customer_id) {
      status_id === 'hot'
        ? (status_id = this.status.CUSTOMER_STATUS_LEAD_PN_HOT)
        : status_id === 'cold'
          ? (status_id = this.status.CUSTOMER_STATUS_LEAD_PN_COLD)
          : (status_id = this.status.CUSTOMER_STATUS_LEAD_PN_DISINTERESTED)

      this.setCustomerLeadStatus({
        customer_id,
        status_id,
      })
        .then(response => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso!',
              text: 'Status atualizado com sucesso.',
              icon: 'CoffeeIcon',
              variant: 'success',
            },
          })
          this.fetchData()
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao atualizar o status. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },

    async toggleFavorite(favorite, customer_id) {
      favorite = favorite === 0 ? 1 : 0
      this.loading = true
      try {
        await this.setFavorite({
          favorite,
          customer_id,
        })
        this.fetchData()
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao atualizar o favorito. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    toggleSelect(customer_id) {
      this.$store.commit(types.TOGGLE_LEAD_SELECTION, customer_id)
    },

    increaseLimit(stageId) {
      const stageLimit = this.limits.find(
        limit => limit.stage_id === stageId,
      )
      if (stageLimit) {
        stageLimit.limit += 5
        this.fetchKanbanData()
      }
    },
    resumeDownload(customer_id) {
      this.downloadResume(customer_id)
        .then(response => {
          setTimeout(() => {
            window.open(response.data, '_blank').focus()
          })
        })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao fazer download. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    disqualifyLead(id, customer_name) {
      this.openDisqualifyLeadSidebar({
        id,
        customer_name,
        saveAction: () => {
          this.fetchData()
        },
      })
    },

    fetchData() {
      if (this.view === 'kanban') {
        this.fetchKanbanData()
      } else {
        this.fetchTableData()
      }
      this.fetchLeadData()
    },

    convertToPn(customer_id, approval_date) {
      this.openSaveConsultantBasicInformationSidebar({
        id: undefined,
        customer_id,
        approval_date,
      })
    },
    openAppointmentForMovedRecruitments(movedRecruitments) {
      const customers = movedRecruitments.map(({ customer_id, name }) => ({
        id: customer_id,
        name,
      }))

      if (customers.length === 1) {
        this.openNewAppointmentSidebar({
          customer_id: customers[0].id,
          eventType: appointmentTypes.INDIVIDUAL_INTERVIEW,
          saveAction: () => {
            this.fetchData()
          },
        })
        return
      }

      this.openNewAppointmentSidebar({
        customers,
        eventType: appointmentTypes.INDIVIDUAL_INTERVIEW,
        saveAction: () => {
          this.fetchData()
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.height-card {
  max-height: 460px !important;
  overflow-y: auto;
}
.w-30 {
  width: 30%;
}
.container-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}
.kanban-board {
  display: flex;
  overflow-x: auto;
  padding: 20px 0;
}
.tabs {
  width: 100%;
  display: flex;
  justify-content: end;
}
.tabs-group {
  width: fit-content;
  display: flex;
  background-color: #283046;
  border-radius: 10px;
}
.tabs-group-dark {
  background-color: #283046;
}
.tabs-group-light {
  background-color: #ffffff;
  .tab-button {
    color: #657c87 !important;
  }
}
.tab-button {
  background-color: transparent;
  border: none;
  color: #cbd5e1;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 10px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  svg {
    margin-right: 5px;
  }
}
.tab-button:hover {
  background-color: #334155;
}
.tab-button.active {
  background-color: #ea580c;
  color: #fff !important;
}
</style>

<style lang="scss">
$themeColor: #f26237;
@import "~vue-slider-component/lib/theme/default.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
.form-group {
  margin-bottom: 0;
}
</style>
